import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Hotel } from 'frontend-utils/models';
import type {
  OtaId,
  Platform,
} from 'frontend-utils/utils/constants/constant-values';
import { getLocalTime } from 'frontend-utils/utils/dayjs';
import type { Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  COMPARETYPES,
  type MealTypeValues,
  type MembershipType,
  type RateParam,
} from 'lumen/utils/constants/general-constants';
import type { TableComparisonLayout } from 'lumen/utils/constants/rate-constants';
import formatDate from 'lumen/utils/format-local-date';

export interface CustomColumn extends CustomEmberTableColumn {
  hotel?: Hotel;
  layout?: TableComparisonLayout;
}

export interface HotelRatesTablesCellsTdBaseSignature {
  Args: {
    cellData: Period;
    column: CustomColumn;
  };
}

export default class HotelRatesTablesCellsTdBase<
  T extends HotelRatesTablesCellsTdBaseSignature =
    HotelRatesTablesCellsTdBaseSignature,
> extends Component<T> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare router: Services['router'];

  get compareType(): string | undefined {
    return this.router.currentRoute?.queryParams['compareType'] as
      | string
      | undefined;
  }

  get compareValue(): string | undefined {
    return this.router.currentRoute?.queryParams['compareValue'] as
      | string
      | undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get day() {
    return this.period?.day;
  }

  get formattedDate(): string {
    return this.day?.date.format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedPrettyDate() {
    if (this.day?.date) {
      return formatDate(this.day.date);
    }
    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    // Hotel is passed as an argument in market/parity/insight/historical
    return this.args.column.hotel ?? this.hotelAndMarketService.hotel;
  }

  get isFuture(): boolean {
    if (this.day?.date) {
      return this.day.date.isAfter(getLocalTime(`${this.timezone}`), 'day');
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPartialLiveupdateOngoing() {
    return this.liveupdate.partialLiveupdatesForThisSelection?.length;
  }

  get isPast(): boolean {
    if (this.day?.date) {
      return this.day.date.isBefore(getLocalTime(`${this.timezone}`), 'day');
    }
    return false;
  }

  get isToday(): boolean {
    if (this.day?.date) {
      return this.day.date.isSame(getLocalTime(`${this.timezone}`), 'day');
    }
    return false;
  }

  get los(): number {
    return Number(
      this.router.currentRoute?.queryParams['los'] ??
        DEFAULT_QP_VALUES.application.los,
    );
  }

  get maxPersons(): number {
    return Number(
      this.router.currentRoute?.queryParams['maxPersons'] ??
        DEFAULT_QP_VALUES.application.maxPersons,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mealPlan() {
    return Number(
      this.router.currentRoute?.queryParams['mealPlan'] ??
        DEFAULT_QP_VALUES.application.mealPlan,
    ) as MealTypeValues;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get membershiptype() {
    return Number(
      this.router.currentRoute?.queryParams['membershipType'] ??
        DEFAULT_QP_VALUES.application.membershipType,
    ) as MembershipType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancy() {
    return this.day?.occupancy;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otaId() {
    return (
      (this.router.currentRoute?.queryParams['otaId'] as OtaId) ??
      DEFAULT_QP_VALUES.application.otaId
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    return this.args.cellData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get platform() {
    return Number(
      this.router.currentRoute?.queryParams['platform'] ??
        DEFAULT_QP_VALUES.application.platform,
    ) as Platform;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateParam() {
    return (
      (this.router.currentRoute?.queryParams['rateParam'] as RateParam) ??
      DEFAULT_QP_VALUES.application.rateParam
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get roomType() {
    return (
      this.router.currentRoute?.queryParams['roomType'] ??
      DEFAULT_QP_VALUES.application.roomType
    );
  }

  get showRateChanges(): boolean {
    const isDateCompare = this.compareType === COMPARETYPES.DATE;
    return isDateCompare && !this.isPast;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timezone() {
    return this.hotel?.hotelinfo.timezone;
  }
}
